import { hasGermlineSets, hasSomaticSets } from './utils';
import { SequenceType, Test, ActiveVariantType } from './types';
import { useActiveTest } from './ActiveTestContext';
import { useActiveVariantType } from './ActiveVariantTypeContext';
import { useEffectOnce } from 'react-use';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelectedSequenceType } from './SequenceTypeContext';
import * as React from 'react';
import queryString from 'query-string';

export type ViewMode = 'DEFAULT' | 'SUMMARY';

export interface OmicsExplorerQueryParams {
  activeSeq?: string;
  viewMode?: ViewMode;
  seqType?: SequenceType;
  varType?: ActiveVariantType;
}

interface UseQueryParamsToInitState {
  tests?: Array<Test>;
}

/**
 * @description A hook for manipulating the URL query parameters for the
 * Omics Explorer.  It watches for changes on important things we care about
 * and adds/removes query params.  It will also set state for us on initial
 * mount based on the provided query params, ensuring they are in fact valid.
 */
export function useAutoQueryParam({ tests }: UseQueryParamsToInitState) {
  const [activeTest, setActiveTest] = useActiveTest();
  const [selectedSequenceType, setSelectedSequenceType] =
    useSelectedSequenceType();
  const [selectedActiveVariantType, setSelectedActiveVariantType] =
    useActiveVariantType();

  const location = useLocation();
  const history = useHistory();

  const {
    activeSeq: activeSeqQueryParam,
    viewMode: viewModeQueryParam,
    seqType: sequenceTypeQueryParam,
    varType: activeVariantTypeQueryParam,
  }: OmicsExplorerQueryParams = queryString.parse(location.search);

  const [viewMode, setViewMode] = React.useState<
    OmicsExplorerQueryParams['viewMode']
  >(
    viewModeQueryParam === 'DEFAULT' || viewModeQueryParam === 'SUMMARY'
      ? viewModeQueryParam
      : 'DEFAULT',
  );

  // Set selected sequence type on mount based on query param
  useEffectOnce(() => {
    if (
      sequenceTypeQueryParam === 'somatic' ||
      sequenceTypeQueryParam === 'germline'
    ) {
      setSelectedSequenceType(sequenceTypeQueryParam);
    }
  });

  // Set view mode on mount based on query param
  useEffectOnce(() => {
    if (viewModeQueryParam === 'DEFAULT' || viewModeQueryParam === 'SUMMARY') {
      setViewMode(viewModeQueryParam);
    } else {
      setViewMode('DEFAULT');
    }
  });

  // Set selected active variant type on mount based on query param
  useEffectOnce(() => {
    if (
      activeVariantTypeQueryParam === 'short' ||
      activeVariantTypeQueryParam === 'copyNumber' ||
      activeVariantTypeQueryParam === 'structural' ||
      activeVariantTypeQueryParam === 'expression'
    ) {
      setSelectedActiveVariantType(activeVariantTypeQueryParam);
    }
  });

  // After we get our tests back, use the active sequence query param
  // to determine our active test
  React.useEffect(() => {
    if (activeTest) {
      return;
    }

    if (!tests) {
      return;
    }

    const testFromQueryParamOrFirst =
      tests?.find(test => test?.id === activeSeqQueryParam) || tests?.[0];

    if (testFromQueryParamOrFirst) {
      setActiveTest(testFromQueryParamOrFirst);
    }

    if (
      testFromQueryParamOrFirst &&
      hasSomaticSets(testFromQueryParamOrFirst) &&
      (!sequenceTypeQueryParam || sequenceTypeQueryParam === 'somatic')
    ) {
      setSelectedSequenceType('somatic');
      return;
    }

    if (
      testFromQueryParamOrFirst &&
      hasGermlineSets(testFromQueryParamOrFirst) &&
      (!sequenceTypeQueryParam || sequenceTypeQueryParam === 'germline')
    ) {
      setSelectedSequenceType('germline');
      return;
    }
  }, [
    activeSeqQueryParam,
    activeTest,
    history,
    location.search,
    sequenceTypeQueryParam,
    setActiveTest,
    setSelectedSequenceType,
    tests,
  ]);

  // auto-query-param view mode updates
  React.useEffect(() => {
    const currentQueryParams: OmicsExplorerQueryParams = {
      ...queryString.parse(location.search),
    };

    if (viewMode === 'SUMMARY' && currentQueryParams.viewMode !== 'SUMMARY') {
      history.push({
        search: queryString.stringify({ viewMode }),
      });
      return;
    }
    if (viewMode === 'DEFAULT' && currentQueryParams?.viewMode) {
      delete currentQueryParams.viewMode;
      history.push({
        search: queryString.stringify(currentQueryParams),
      });
    }
  }, [history, location.search, viewMode]);

  // Handle query paraming the active sequence
  React.useEffect(() => {
    if (!activeTest?.id) {
      return;
    }

    const qps: OmicsExplorerQueryParams = {
      ...queryString.parse(location.search),
    };

    if (viewMode === 'DEFAULT' && qps?.activeSeq !== activeTest?.id) {
      qps.activeSeq = activeTest?.id;
      history.push({ search: queryString.stringify(qps), hash: undefined });
    }
  }, [activeTest, history, location.search, viewMode]);

  // State Handlers
  const updateViewMode = (
    newViewMode: OmicsExplorerQueryParams['viewMode'],
  ) => {
    setViewMode(newViewMode);
  };

  const updateSelectedSequenceType = (newSequenceType: SequenceType) => {
    setSelectedSequenceType(newSequenceType);

    history.push({
      search: queryString.stringify({
        ...queryString.parse(location.search),
        seqType: newSequenceType,
      }),
      hash: undefined,
    });
  };

  const updateSelectedTest = (test: Test) => {
    setActiveTest(test);

    const qps: OmicsExplorerQueryParams = {
      ...queryString.parse(location.search),
    };

    // Check if test has somatic or germline and set
    const hasSomaticOptions = hasSomaticSets(test);
    const hasGermlineOptions = hasGermlineSets(test);

    if (selectedSequenceType === 'somatic' && hasSomaticOptions) {
      setSelectedSequenceType('somatic');
      history.push({
        search: queryString.stringify({
          ...qps,
          seqType: 'somatic',
        }),
        hash: undefined,
      });
      return;
    }

    if (
      selectedSequenceType === 'somatic' &&
      !hasSomaticOptions &&
      hasGermlineOptions
    ) {
      setSelectedSequenceType('germline');
      history.push({
        search: queryString.stringify({
          ...qps,
          seqType: 'germline',
        }),
        hash: undefined,
      });
      return;
    }

    if (
      selectedSequenceType === 'germline' &&
      !hasGermlineOptions &&
      hasSomaticOptions
    ) {
      setSelectedSequenceType('somatic');
      history.push({
        search: queryString.stringify({
          ...qps,
          seqType: 'somatic',
        }),
        hash: undefined,
      });
      return;
    }
  };

  const updateActiveVariantType = (newActiveVariantType: ActiveVariantType) => {
    setSelectedActiveVariantType(newActiveVariantType);

    history.push({
      search: queryString.stringify({
        ...queryString.parse(location.search),
        varType: newActiveVariantType,
      }),
      hash: undefined,
    });
  };

  return {
    activeTest,
    selectedSequenceType,
    viewMode,
    selectedActiveVariantType,
    setActiveTest,
    updateViewMode,
    updateSelectedSequenceType,
    updateSelectedTest,
    updateActiveVariantType,
  };
}
